import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DeviceMobile,
  CheckCircle,
  WarningCircle,
  ArrowLeft,
  Envelope,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listTeachersInstallStatus,
  lastSeenLabel,
} from "@/lib/teacherInstalls";

/**
 * Admin · /admin/teacher-installs
 *
 * Lists every teacher in the org with their install/push state. Mobile-
 * first single-column card layout; scales to a 2-col grid on desktop.
 *
 * Actions per row:
 *   - Copy email to clipboard (so admin can drop it in a team channel).
 *   - Open DM: deep-links to /admin/messaging (inbox). We don't auto-
 *     start a conversation because the existing messaging flow already
 *     handles discovery; keeping scope tight.
 *
 * No seed / no write paths yet — this is a read-only visibility surface
 * that piggybacks on the new rpc_list_teachers_install_status RPC.
 */
export default function AdminTeacherInstalls() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending"); // 'pending' | 'all'

  const load = useCallback(async () => {
    const data = await listTeachersInstallStatus();
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return filter === "pending" ? rows.filter((r) => !r.has_push) : rows;
  }, [rows, filter]);

  const total = rows?.length ?? 0;
  const pending = rows?.filter((r) => !r.has_push).length ?? 0;

  async function copyEmail(email) {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied ${email}`);
    } catch {
      toast.error("Could not copy — try long-pressing the email");
    }
  }

  return (
    <div data-testid="admin-teacher-installs" className="space-y-6">
      <header>
        <Link
          to="/admin"
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-neutral-800"
        >
          <ArrowLeft size={12} weight="bold" />
          Back to overview
        </Link>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Teacher mobile rollout
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          App install status
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Teachers who install the app on their phone get instant push alerts
          when a parent messages them. Nudge anyone on the waiting list below.
        </p>
      </header>

      {/* Summary band */}
      <section
        data-testid="admin-teacher-installs-summary"
        className="grid grid-cols-2 gap-3 sm:grid-cols-3"
      >
        <SummaryPill
          testId="admin-teacher-installs-summary-total"
          icon={DeviceMobile}
          label="Teachers"
          value={rows === null ? "…" : total}
        />
        <SummaryPill
          testId="admin-teacher-installs-summary-installed"
          icon={CheckCircle}
          label="Installed"
          value={rows === null ? "…" : total - pending}
          tone="primary"
        />
        <SummaryPill
          testId="admin-teacher-installs-summary-pending"
          icon={WarningCircle}
          label="Pending"
          value={rows === null ? "…" : pending}
          tone={pending > 0 ? "warn" : "muted"}
        />
      </section>

      {/* Filter tabs */}
      <div
        data-testid="admin-teacher-installs-filter"
        role="tablist"
        className="flex items-center gap-2"
      >
        {[
          { key: "pending", label: "Not installed" },
          { key: "all", label: "All teachers" },
        ].map((t) => {
          const active = t.key === filter;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`admin-teacher-installs-filter-${t.key}`}
              data-active={active}
              onClick={() => setFilter(t.key)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                active
                  ? "border-primary/50 bg-primary/10 text-primary-hover"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      {rows === null ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <ul
          data-testid="admin-teacher-installs-list"
          className="grid grid-cols-1 gap-3 md:grid-cols-2"
        >
          {filtered.map((r) => (
            <TeacherCard key={r.profile_id} row={r} onCopyEmail={copyEmail} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SummaryPill({ icon: Icon, label, value, tone = "neutral", testId }) {
  const palette =
    tone === "primary" ? "bg-primary/10 text-primary-hover border-primary/30" :
    tone === "warn"    ? "bg-[#C6851E]/10 text-[#C6851E] border-[#C6851E]/30" :
    tone === "muted"   ? "bg-neutral-50 text-neutral-500 border-neutral-200" :
    /* neutral */         "bg-white text-neutral-700 border-neutral-200";
  return (
    <article
      data-testid={testId}
      className={`flex items-center gap-3 rounded-2xl border p-3 ${palette}`}
    >
      <Icon size={18} weight="duotone" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-70">
          {label}
        </p>
        <p className="font-heading text-xl font-semibold leading-tight">
          {value}
        </p>
      </div>
    </article>
  );
}

function EmptyState({ filter }) {
  const copy = filter === "pending"
    ? {
        icon: CheckCircle,
        title: "Every teacher is set up",
        note: "Every teacher in your organization has installed the app and enrolled a device for push alerts.",
      }
    : {
        icon: DeviceMobile,
        title: "No teachers yet",
        note: "Once you add teachers in Staff, they'll show up here for install tracking.",
      };
  const Icon = copy.icon;
  return (
    <div
      data-testid="admin-teacher-installs-empty"
      className="rounded-2xl border border-neutral-200 bg-white p-12 text-center"
    >
      <Icon size={32} weight="duotone" className="mx-auto text-primary-hover" />
      <p className="mt-3 font-heading text-base font-semibold text-neutral-900">
        {copy.title}
      </p>
      <p className="mt-1 text-sm text-neutral-500">{copy.note}</p>
    </div>
  );
}

function TeacherCard({ row, onCopyEmail }) {
  const installed = !!row.has_push;
  return (
    <li
      data-testid={`admin-teacher-installs-row-${row.profile_id}`}
      data-installed={installed}
      className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
    >
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${
          installed
            ? "bg-primary/10 text-primary-hover"
            : "bg-[#C6851E]/10 text-[#C6851E]"
        }`}
      >
        {installed
          ? <CheckCircle size={18} weight="duotone" />
          : <WarningCircle size={18} weight="duotone" />
        }
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-semibold text-neutral-900">
          {row.full_name || row.email?.split("@")[0] || "Teacher"}
        </p>
        <p className="mt-0.5 truncate text-xs text-neutral-500">{row.email}</p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
          <span
            data-testid={`admin-teacher-installs-row-${row.profile_id}-status`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              installed
                ? "border-primary/30 bg-primary/10 text-primary-hover"
                : "border-[#C6851E]/30 bg-[#C6851E]/5 text-[#C6851E]"
            }`}
          >
            {installed ? "Installed" : "Not installed"}
          </span>
          {installed && (
            <span>
              {row.device_count} {row.device_count === 1 ? "device" : "devices"}
              {row.last_push_at && <> · last seen {lastSeenLabel(row.last_push_at)}</>}
            </span>
          )}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid={`admin-teacher-installs-row-${row.profile_id}-copy`}
            onClick={() => onCopyEmail(row.email)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 transition hover:bg-neutral-50"
          >
            <Envelope size={11} weight="duotone" />
            Copy email
          </button>
          <Link
            to="/admin/messaging"
            data-testid={`admin-teacher-installs-row-${row.profile_id}-dm`}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 transition hover:bg-neutral-50"
          >
            <ArrowSquareOut size={11} weight="duotone" />
            Open messaging
          </Link>
        </div>
      </div>
    </li>
  );
}
